import * as React from "react";
import {useEffect, useRef, useState} from "react";

import actionsModel from "./actions/list_actions_model";
import ListOfActions from "./actions/list_of_actions";

import locationsModel from "./locations/locations_model";
import ListOfLocations from "./locations/list_of_locations";

import bookmarksModel from "./bookmarks/bookmarks_model";
import ListOfBookmarks from "./bookmarks/list_of_bookmarks";

import {Tab, TabList, TabPanel, Tabs} from "react-tabs";

import FilterInput from "./filter_input";
import KeyboardNavigation from "./keyboard_navigation";
import tag_completion from "../tag_completion_on_click";

export type ShiftShiftProps = {

    /**
     * Initial text to set in filter field and to filter by
     */
    filterText?: string;

    /**
     * If set, item with this ID will be activated if it is present in the actions list
     */
    idToActivate?: string;
    tabToActivate?: TabCode;

    preferred?: string[];
    selectionText: string;

    maxHeightCss? : string;
    cutItemId? : number;  // task_id of the item which was cut

    updateHeightCallback(): void;
}


const models: ShiftShiftModel[] = [locationsModel, bookmarksModel, actionsModel];

const calcModelIndex = (tabToActivate: TabCode) => {
    const tab:TabCode = tabToActivate || sessionStorage.getItem("last_search_tab") as TabCode || 'actions';
    let result = 0;
    models.forEach((m, idx) => {
        if (m.code === tab) {
            result = idx;
        }
    })
    return result;
}

export const ShiftShift = (props: ShiftShiftProps) => {

    const bookmarks_enabled = maxkir.Bookmarks;
    if (!bookmarks_enabled) {
        // Remove bookmarks model
        const idx = models.indexOf(bookmarksModel);
        if (idx >= 0) {
            models.splice(idx, 1);
        }
    }

    const [tabIndex, _setTabIndex] = useState(calcModelIndex(props.tabToActivate));
    const currentModel = models[tabIndex];
    const [currentText, setCurrentText] = useState(props.filterText);

    // Need this to re-focus input field on tabs change
    const [focusCounter, updateFocusCounter] = useState(0);

    const changeTab = (newIndex) => {
        updateFocusCounter(focusCounter + 1);
        _setTabIndex(newIndex);
    }

    const [lastModelUpdate, setLastModelUpdate] = useState(null);


    const [selectedIndex, setSelectedIndex] = useState(0);

    // Update window size when search result change
    useEffect(() => {
        props.updateHeightCallback();
    }, [lastModelUpdate, tabIndex])

    const renderedItemsCount = useRef(0);

    const changeCurrentTab = (direction) => {
        let newTabIndex = tabIndex + direction
        if (newTabIndex >= models.length) {
            newTabIndex = 0;
        }
        if (newTabIndex < 0) {
            newTabIndex = models.length - 1;
        }
        changeTab(newTabIndex);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Tab') {
            changeCurrentTab(e.shiftKey ? -1 : 1)
            e.preventDefault();
        }

        // Handle mnemonics for tabs
        if (e.altKey) {
            let key = e.code;
            if (key.startsWith("Key")) {
                key = key.substring(3);
            }
            const tabs = document.querySelectorAll(".react-tabs__tab");
            let idx = 0;
            tabs.forEach((tab: HTMLElement) => {
                if (tab.dataset.mnemonic === key) {
                    changeTab(idx)
                }
                idx ++;
            })
        }

        const keyboard = new KeyboardNavigation({
            getItemsCount(): number {
                return renderedItemsCount.current;
            }, getSelectedIndex(): number {
                return selectedIndex;
            }, setSelectedIndex(idx: number): void {
                setSelectedIndex(idx);
            },
            onLeft() { if (currentModel === locationsModel ) {
                let selectionId = locationsModel.backState();
                if (selectionId >= 0) {
                    setSelectedIndex(selectionId);
                }
            } },
            onRight() { if (currentModel === locationsModel ) {
                locationsModel.forwardState();
            } }
        });

        keyboard.onKeyDown(e);
    };

    const saveStateForFuture = (text) => {
        maxkir.set_flag("last_search_txt_" + currentModel.code, text, 3600);
        sessionStorage.setItem("last_search_tab", currentModel.code);
    };

    const onFilterTextChanged = (text, memorizeValue) => {
        setCurrentText(text);
        currentModel.onTextChanged(text, true);
        if (memorizeValue || !text) {
            saveStateForFuture(text);
        }
    }

    const saveStateAndClosePopups = (saveFilterText: boolean) => {
        saveStateForFuture(saveFilterText ? currentText : null);
        maxkir.cmd && maxkir.cmd.close_all_popups();
    }

    // Global initial init and dispose for all models
    useEffect(() => {
        return () => {
            models.forEach(m => m.dispose())
        }
    }, [])

    // Per-tab init
    useEffect(() => {
        const modelUpdated = () => {
            setLastModelUpdate(new Date()); // To trigger UI refresh

            if (currentModel.shouldResetSelection()) {
                setSelectedIndex(0);
            }

            // The model can change the filter text in the field
            setCurrentText(currentModel.getFilterTerms().join(" "));
        }

        actionsModel.setPreferredActionsOrder(props.preferred || []);

        currentModel.addDataAvailableCallback(modelUpdated);

        currentModel.onTextChanged(currentText || "", false); // Initial filtering with explicit text if needed

        return () => {
            currentModel.removeDataAvailableCallback(modelUpdated);
        }
    }, [tabIndex])


    const tagCompletionOnClick = (event) => {
        tag_completion(event, currentText, (newText) => {
            onFilterTextChanged(newText, false);
            // To focus input element:
            updateFocusCounter(focusCounter + 1);
        })
    }

    const locationsActionCallback = (list_id: number, task_id: number, filterText: string, el: HTMLElement, e?: Event) => {
        if (el) {
            el = el.querySelector(".goLink") || el;
            maxkir.click_link(el, false);
        }
        else {
            // New list was created
            maxkir.add_checklist.on_create({id: list_id}, filterText === "");
        }
    }


    const styles = { "--searchEverywhere-maxHeight":
            (props.maxHeightCss ? props.maxHeightCss : "250px")} as React.CSSProperties;

    return <div className="searchEverywhere" style={styles} tabIndex={-1} onKeyDown={onKeyDown}
                onClick={tagCompletionOnClick}>

        <Tabs onSelect={index => changeTab(index)} selectedIndex={tabIndex}>
            <div className={"searchEverywhere__header"}>
                <TabList>
                    <Tab className={["react-tabs__tab--wide", "react-tabs__tab", "tab_locations"]}
                         title={I18N('js_search_everywhere_title_locations')}
                         data-mnemonic={"L"}
                    >{I18N('js_search_everywhere_tab_locations')}</Tab>
                    {bookmarks_enabled && <Tab className={["react-tabs__tab", "tab_bookmarks"]}
                                               title={I18N('js_search_everywhere_title_bookmarks')}
                                               data-mnemonic={"B"}
                    >{I18N('js_search_everywhere_tab_bookmarks')}</Tab>}
                    <Tab className={["react-tabs__tab", "tab_actions"]}
                         title={I18N('js_search_everywhere_title_actions')}
                         data-mnemonic={"C"}
                    >{I18N('js_search_everywhere_tab_actions')}</Tab>
                </TabList>
                <span dangerouslySetInnerHTML={{__html: I18N('js_cmd.search_everywhere.shortcut')}}
                title={"Shift+Shift"}/>

            </div>
            <FilterInput filterText={currentText} onTextChanged={onFilterTextChanged} setFocus={focusCounter}
                         placeholder={I18N('js_search_everywhere_filter_placeholder')}
                         isChanged={currentText !== props.filterText}
                         inProgress={currentModel.isLoading()}
            />
            <TabPanel>
                <ListOfLocations
                    model={locationsModel}

                    onBeforeItemOpen={saveStateAndClosePopups}
                    actionCallback={locationsActionCallback}

                    selectedIndex={selectedIndex}
                    cutItemId={props.cutItemId}

                    renderedItemsCountCallback={(x) => { renderedItemsCount.current = x }}
                    idToActivate={props.idToActivate}
                    isActiveTab={locationsModel == currentModel}
                />
            </TabPanel>
            {
              bookmarks_enabled &&
              <TabPanel>
                <ListOfBookmarks model={bookmarksModel}
                                 onBeforeItemOpen={saveStateAndClosePopups}

                                 selectedIndex={selectedIndex}
                                 renderedItemsCountCallback={(x) => {
                                     renderedItemsCount.current = x
                                 }}
                                 idToActivate={props.idToActivate}
                                 isActiveTab={bookmarksModel == currentModel}
                />
              </TabPanel>
            }
            <TabPanel>
                <ListOfActions
                    items={actionsModel.getFoundItems()}
                    filterTerms={currentModel.getFilterTerms()}

                    onBeforeItemOpen={saveStateAndClosePopups}
                    updateHeightCallback={props.updateHeightCallback}

                    selectedIndex={selectedIndex}

                    selectionText={props.selectionText}

                    renderedItemsCountCallback={(x) => { renderedItemsCount.current = x }}
                    idToActivate={props.idToActivate}
                    isActiveTab={actionsModel == currentModel}
                />
            </TabPanel>
        </Tabs>

    </div>
}



